import React, { useEffect, useState } from 'react';

//====================Importaciones======================//
import { useIntl } from 'react-intl'; // Para la traducción de los textos
import { useDispatch, useSelector } from 'react-redux'; // Para manejar el estado de Redux
import { Dashboard as DashboardLayout } from 'layouts'; // Layout de la aplicación
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid'; // Componente para la tabla
import styles from './styles'; // Estilos personalizados
import { makeStyles } from '@mui/styles'; // Estilos con makeStyles
import { Box, Fab, Tooltip, Zoom } from '@mui/material'; // Material-UI components (Botones flotantes, Tooltip, etc.)
import { getColumns } from './columns'; // Función que obtiene las columnas de la tabla
import { deleteUser, showMetimurUsers } from 'js/actions/userActions'; // Acciones de Redux para eliminar y mostrar usuarios
import { Add, Settings } from '@mui/icons-material'; // Iconos para botones
import Swal from 'sweetalert2'; // Librería para los popups

const useStyles = makeStyles(styles); // Usamos los estilos definidos

//====================Componentes======================//

// Componente de la barra de búsqueda rápida en el toolbar
function QuickSearchToolbar() {
  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <GridToolbarQuickFilter /> {/* Barra de búsqueda rápida de la tabla */}
    </Box>
  );
}

export default function UsersMetimur() {
  //====================Variables======================//
  const classes = useStyles(); // Usamos los estilos
  const intl = useIntl(); // Acceso a la internacionalización
  const dispatch = useDispatch(); // Usamos dispatch de Redux
  const { users } = useSelector((state) => state.user); // Obtiene los usuarios del estado de Redux
  const { user } = useSelector((state) => state.user); // Obtiene el usuario actual
  const [hovered, setHovered] = useState(false); // Estado para manejar el hover de los botones flotantes

  //====================Efectos======================//
  useEffect(() => {
    // Cargar los usuarios de Metimur cuando se monte el componente
    dispatch(showMetimurUsers());
  }, [dispatch]);

  //====================Funciones======================//
  // Función que maneja la eliminación de un usuario
  const handleDeleteUser = (user) => {
    // Muestra un cuadro de confirmación antes de borrar al usuario
    console.log(user);
    Swal.fire({
      title: `${intl.formatMessage({ id: 'app.groupsCards.deleteGroupBtn.title' })} `,
      text: `${intl.formatMessage({ id: 'app.groupsCards.deleteGroupBtn.text' })} ${user.firstname}`,
      icon: 'warning',
      showCancelButton: true, // Muestra botón de cancelación
      confirmButtonText: intl.formatMessage({
        id: 'app.groupsCards.deleteGroupBtn.confirmation',
      }),
    }).then((result) => {
      if (result.value) {
        // Si el usuario confirma, se elimina
        Swal.fire({
          title: intl.formatMessage({
            id: 'app.groupsCards.deleteGroupBtn.titleConfirmation',
          }),
          text: intl.formatMessage({
            id: 'app.groupsCards.deleteGroupBtn.textConfirmation',
          }),
          icon: 'success', // Mensaje de éxito
        });
        dispatch(deleteUser(user.id)); // Llama la acción de Redux para eliminar al usuario
      }
    });
  };

  //====================Columnas de la Tabla======================//
  // Función que obtiene las columnas para la tabla
  const columns = getColumns(intl, user, handleDeleteUser);

  //====================Renderizado======================//
  return (
    <DashboardLayout title="Usuarios Metimur">
      <div className={classes.root}>
        <div className={classes.content}>
          {/* DataGrid para mostrar la lista de usuarios */}
          <DataGrid
            rows={users} // Pasa los usuarios como filas
            columns={columns} // Pasa las columnas configuradas
            // Deshabilitar la seleccion de filas
            rowSelection={false}
            initialState={{
              ...users.initialState,
              pagination: {
                ...users.initialState?.pagination,
                paginationModel: {
                  pageSize: 5, // Configura el tamaño de página
                },
              },
            }}
            slots={{ toolbar: QuickSearchToolbar }} // Usa el componente de búsqueda rápida
            sx={{
              pt: 2,
              pb: 2,
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              '& .MuiDataGrid-root': {
                border: 'none', // Quita el borde
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 1)', // Borde inferior de las celdas
                maxHeight: '60px', // Máxima altura de las celdas
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid rgb(199, 199, 199)', // Borde inferior de los encabezados
              },
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid rgb(199, 199, 199)', // Borde inferior de las filas
                maxHeight: '60px', // Máxima altura de las filas
              },
            }}
          />
        </div>
      </div>

      {/* Botones flotantes */}
      <Box
        sx={{
          position: 'fixed',
          bottom: '5vh', // Coloca los botones flotantes al fondo
          right: '1vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          m: '1em',
        }}
        onMouseEnter={() => setHovered(true)} // Cuando el mouse entra, cambia el estado de hover
        onClick={() => setHovered(!hovered)} // Al hacer click, cambia el estado de hover
        onMouseLeave={() => setHovered(false)} // Cuando el mouse sale, cambia el estado de hover
      >
        {/* Botón de agregar usuario */}
        <Zoom in={hovered}>
          <Tooltip
            title={intl.formatMessage({ id: 'app.metusers.addUserBtn' })}
          >
            <Fab
              href="/newMetimurUser" // Enlace al formulario de agregar usuario
              sx={{
                backgroundColor: '#00AFEF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#0095D4', // Cambia el color al pasar el mouse
                },
              }}
            >
              <Add /> {/* Icono de agregar */}
            </Fab>
          </Tooltip>
        </Zoom>
        {/* Botón de configuración */}
        <Tooltip title="Configuración">
          <Fab
            sx={{
              backgroundColor: '#A53693',
              color: 'white',
              width: 80,
              height: 80,
              '&:hover': {
                backgroundColor: '#6A1A5F',
              },
            }}
            aria-label="settings"
            onMouseEnter={() => setHovered(true)} // Cambia el estado de hover
            onClick={() => setHovered(!hovered)} // Al hacer click, cambia el estado de hover
          >
            <Settings
              sx={{
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
                transform: hovered ? 'rotate(180deg)' : 'rotate(0deg)',
                width: 40,
                height: 40,
              }}
            />
          </Fab>
        </Tooltip>
      </Box>
    </DashboardLayout>
  );
}
